import axios, { AxiosError, AxiosResponse, AxiosRequestHeaders, InternalAxiosRequestConfig } from 'axios'
import { ref } from 'vue'
import Cookies from 'js-cookie'
import { Store } from 'vuex'
import { State } from '@/types'

axios.defaults.baseURL = '/submission'

export const lastError = ref<AxiosError | null>(null)

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  __retryCount?: number
  __isRetry?: boolean
}

let storeInstance: Store<State>

export function setStoreInstance(store: Store<State>) {
  storeInstance = store
}

// Request interceptor: Add JWT token
axios.interceptors.request.use((config) => {
  if (!config.headers) {
    config.headers = {} as AxiosRequestHeaders
  }
  config.headers.Accept = 'application/json'

  const token = Cookies.get('auth')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor: Handle 401 errors and retries
axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const config = error.config as ExtendedAxiosRequestConfig

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && config && !config.__isRetry) {
      try {
        const submissionId = storeInstance?.state?.activeSubmission?.submissionId
        if (submissionId) {
          await storeInstance.dispatch('refreshToken', submissionId)
          config.__isRetry = true

          // Update token in config headers and retry the request
          config.headers = config.headers || {}
          config.headers.Authorization = `Bearer ${Cookies.get('auth')}`
          return axios(config)
        }
      }
      catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        lastError.value = error
        return Promise.reject(error)
      }
    }

    // Retry GET requests (excluding 401s)
    if (config?.method === 'get' && error.response?.status !== 401) {
      const retryCount = (config.__retryCount || 0) + 1
      if (retryCount < 3) {
        config.__retryCount = retryCount
        await new Promise(res => setTimeout(res, retryCount * 500))
        return axios(config)
      }
    }

    lastError.value = error
    return Promise.reject(error)
  },
)

export default axios
